/**
 * A domain that isn't active: expired (2056-382, recovery 4090-7190), recovering (2060-383), no
 * longer recoverable (4090-9224), transferring away (2054-394), transferred away (2054-774), and the
 * failed / removed leftovers.
 */
import { useMutation, useQueryClient } from "@tanstack/react-query";
import { useState, type ReactNode } from "react";
import { Link, useNavigate } from "react-router";

import { HelpHint } from "@/components/help";
import { HELP_KEYS } from "@/lib/help-context";
import { useOrganization } from "@/auth/organization";
import { RenewalRiskWarning } from "@/components/billing/SaveCardChoice";
import { PoweredByStripe } from "@/components/billing/PoweredByStripe";
import { CardBadge, PaymentMethodModal, defaultMethod, usePaymentMethods } from "@/components/domains/shared";
import { GetHelpLink } from "@/components/support/GetHelpLink";
import {
  ErrorAlert,
  Badge,
  Modal,
  KeyValue,
  Button,
  buttonClass,
  Breadcrumb,
  Section,
  useToast,
  type Requirement,
} from "@/components/ui";
import {
  daysUntil,
  domainInitials,
  domainKeys,
  domainsApi,
  has,
  healthPill,
  useDomainDetail,
  type DomainDetail,
} from "@/lib/domains";
import { formatMoney } from "@/lib/format";
import { attemptFinished, attemptKey } from "@/lib/attemptKey";
import type { PaymentMethod } from "@/lib/types";

import { pendingOp, shownError, useConfirmed } from "./security/shared";
import {
  StepPill,
  Heading,
  HeadTile,
  DomainPage,
  TwoColumns,
  RuleRow,
  ProgressSection,
  Notice,
  Hint,
  type Step,
} from "@/components/domains/parts";

/** Changing a domain needs domains:manage on its folder; without it the actions show, unavailable. */
const manageOn = (detail: DomainDetail): Requirement => ({
  permission: "domains:manage",
  on: ["domain_folder", detail.folder?.id],
});

const CRUMB: Partial<Record<DomainDetail["domain"]["status"], string>> = {
  expired: "expired",
  restoring: "recovering",
  unrecoverable: "unable to recover",
  transferring_out: "pending",
  transferred_out: "transferred",
  failed: "failed",
  released: "removed",
  registering: "registering",
  transferring_in: "transferring",
};

const inDays = (n: number | null) =>
  n === null ? "—" : n <= 0 ? "Today" : n === 1 ? "Tomorrow" : `In ${n} days`;
const sld = (fqdn: string) => fqdn.split(".")[0] ?? fqdn;

export function LifecycleView({ detail }: { detail: DomainDetail }) {
  const d = detail.domain;
  const waiting =
    d.status === "restoring" ||
    d.status === "transferring_out" ||
    d.status === "registering" ||
    d.status === "transferring_in";
  // Keep refetching while the registry is still working on it; the shell re-renders on change.
  useDomainDetail(d.id, { poll: waiting });

  const pill = healthPill(d.health);
  const view = body(detail);
  return (
    <DomainPage>
      <Breadcrumb
        items={[
          { label: "Domains", to: "/domains" },
          { label: "...", to: "/domains" },
          { label: d.display_name },
          { label: CRUMB[d.status] ?? d.status },
        ]}
      />
      <Heading
        size={24}
        tile={<HeadTile>{domainInitials(d.fqdn)}</HeadTile>}
        title={d.display_name}
        status={
          <Badge size="md" tone={pill.tone}>
            {pill.label}
          </Badge>
        }
        subtitle={view.subtitle}
      />
      {view.content}
    </DomainPage>
  );
}

function body(detail: DomainDetail): { subtitle: ReactNode; content: ReactNode } {
  const d = detail.domain;
  switch (d.status) {
    case "expired":
      return {
        subtitle: d.site
          ? "Your website on this domain has stopped working"
          : "This domain has stopped working",
        content: <Expired detail={detail} />,
      };
    case "restoring":
      return {
        subtitle: "Your redemption payment was received — the registry is restoring this domain",
        content: (
          <>
            <Notice tone="processing" title="Restoring your domain...">
              This usually completes within a few hours, but can take up to 48 hours depending on the
              registry. This page updates the moment it's back.
            </Notice>
            <ProgressSection
              title="Recovery progress"
              steps={[
                { label: "Redemption fee paid", state: "completed" },
                { label: "Recovery request sent to registry", state: "completed" },
                { label: "Registry restoring domain", state: "in_progress" },
                { label: "DNS and services reconnected", state: "not_yet" },
              ]}
            />
          </>
        ),
      };
    case "registering":
    case "transferring_in":
      return {
        subtitle:
          d.status === "registering"
            ? "The registry is still registering this domain"
            : "The transfer is still in progress",
        content: (
          <Notice
            tone="processing"
            title={d.status === "registering" ? "Registering your domain..." : "Transferring your domain..."}
            actions={
              <Link to="/domains/orders" className={buttonClass("secondary", "lg")}>
                View order history
              </Link>
            }
          >
            This page updates on its own as soon as the registry confirms.
          </Notice>
        ),
      };
    case "unrecoverable":
      return {
        subtitle: "This domain will be permanently deleted and released for anyone to register",
        content: <Unrecoverable detail={detail} />,
      };
    case "transferring_out":
      return {
        subtitle: "Waiting on the new registrar to complete the transfer",
        content: <TransferringOut detail={detail} />,
      };
    case "transferred_out":
      return {
        subtitle: "Ownership has moved to your new registrar",
        content: <TransferredOut detail={detail} />,
      };
    case "failed":
      return {
        subtitle: "We couldn't complete this registration",
        content: (
          <Notice
            tone="danger"
            title={`${d.display_name} wasn't registered`}
            actions={
              <>
                <Link to="/domains/orders" className={buttonClass("secondary", "lg")}>
                  View order history
                </Link>
                <Link
                  to={`/domains/find?q=${encodeURIComponent(sld(d.fqdn))}`}
                  className={buttonClass("secondary", "lg")}
                >
                  Find a domain
                </Link>
                <RemoveButton detail={detail} variant="secondary" />
              </>
            }
          >
            The registry didn't complete the registration. Any payment for it is refunded automatically — the
            order shows the details.
          </Notice>
        ),
      };
    default:
      return {
        subtitle: "No longer managed by CortexGrip",
        content: (
          <Notice
            tone="cream"
            title={`${d.display_name} was removed from My Domains`}
            actions={
              <>
                <Link to="/domains" className={buttonClass("secondary", "lg")}>
                  Back to My Domains
                </Link>
                <Link
                  to={`/domains/find?q=${encodeURIComponent(sld(d.fqdn))}`}
                  className={buttonClass("secondary", "lg")}
                >
                  Find a domain
                </Link>
              </>
            }
          >
            CortexGrip doesn't manage this domain any more, so there's nothing left to change here.
          </Notice>
        ),
      };
  }
}

// ── expired ─────────────────────────────────────────────────────────────────
function Expired({ detail }: { detail: DomainDetail }) {
  const d = detail.domain;

  const [open, setOpen] = useState(false);
  const expiredDays = Math.max(0, -(daysUntil(d.expires_at) ?? 0));
  const graceLeft = daysUntil(d.recoverable_until);
  const recoverable = has(detail, "restore") && (graceLeft === null || graceLeft >= 0);
  const fee = d.restore_price_cents;

  return (
    <>
      <Notice
        tone="danger"
        title={`This domain expired ${expiredDays === 0 ? "today" : `${expiredDays} ${expiredDays === 1 ? "day" : "days"} ago`}`}
        actions={
          recoverable ? (
            <Button variant="brand" size="lg" requires={manageOn(detail)} onClick={() => setOpen(true)}>
              Recover this domain
            </Button>
          ) : (
            <GetHelpLink
              context={{
                resourceType: "domain",
                resourceId: d.fqdn,
                reason: "Domain past its recovery window",
              }}
            />
          )
        }
      >
        {recoverable
          ? `${d.display_name} is still recoverable, but a redemption fee applies after expiry.`
          : `${d.display_name} can't be recovered through CortexGrip — contact support and we'll check what the registry allows.`}
        {d.site ? ` Your website ${d.site.name} is offline on this domain until this is resolved.` : ""}
      </Notice>
      <TwoColumns
        main={
          <Section
            title={
              <>
                Lifecycle status{" "}
                <HelpHint helpKey={HELP_KEYS.domains.expiryAndRecovery} label="expiry and recovery" />
              </>
            }
          >
            <KeyValue variant="detail" label="Expired">
              <Badge size="md" tone="danger">
                {expiredDays === 0 ? "Today" : `${expiredDays} days ago`}
              </Badge>
            </KeyValue>
            <KeyValue variant="detail" label="Grace period">
              <Badge size="md" tone="warning">
                {graceLeft === null ? "—" : graceLeft <= 0 ? "Ends today" : `Ends in ${graceLeft} days`}
              </Badge>
            </KeyValue>
            <KeyValue variant="detail" label="Redemption fee">
              <Badge size="md" tone="neutral">
                {fee !== null
                  ? `${formatMoney(fee, detail.currency)} + VAT if recovered now`
                  : "Shown at checkout"}
              </Badge>
            </KeyValue>
            <KeyValue variant="detail" label="Deletes permanently">
              <Badge size="md" tone="neutral">
                {inDays(daysUntil(d.deleted_at))}
              </Badge>
            </KeyValue>
          </Section>
        }
        side={
          <Section title="What's affected">
            {d.site && (
              <KeyValue variant="detail" label="Website">
                <Badge size="md" tone="danger">
                  Offline
                </Badge>
              </KeyValue>
            )}
            <KeyValue variant="detail" label="Billing">
              <Badge size="md" tone="neutral">
                No new charges until recovered
              </Badge>
            </KeyValue>
          </Section>
        }
      />
      <RecoverModal detail={detail} open={open} onClose={() => setOpen(false)} graceLeft={graceLeft} />
    </>
  );
}

function RecoverModal({
  detail,
  open,
  onClose,
  graceLeft,
}: {
  detail: DomainDetail;
  open: boolean;
  onClose: () => void;
  graceLeft: number | null;
}) {
  const d = detail.domain;
  const { orgId } = useOrganization();
  const qc = useQueryClient();
  const navigate = useNavigate();
  const methods = usePaymentMethods();
  const [chosen, setChosen] = useState<PaymentMethod | null>(null);
  const [picking, setPicking] = useState(false);
  const card = chosen ?? defaultMethod(methods.data?.payment_methods);
  const fee = d.restore_price_cents;
  const money = fee !== null ? formatMoney(fee, detail.currency) : null;

  // One hidden key per recovery of this domain: sending it twice pays once.
  const restore = useMutation({
    mutationFn: () => domainsApi.restore(orgId, d.id, card?.id ?? null, attemptKey("restore", d.id)),
    onError: (err) => attemptFinished("restore", d.id, err),
    onSuccess: async (order) => {
      attemptFinished("restore", d.id);
      await qc.invalidateQueries({ queryKey: domainKeys.all(orgId) });
      navigate(`/domains/orders/${order.id}`);
    },
  });

  return (
    <>
      <Modal
        open={open && !picking}
        onClose={onClose}
        title="Confirm domain recovery"
        subtitle={`${d.display_name} - redemption fee applies since it's past expiry`}
        note="Charged immediately upon confirmation."
        footer={
          <>
            <Button variant="secondary" size="lg" onClick={onClose}>
              Cancel
            </Button>
            <Button
              variant="brand"
              size="lg"
              loading={restore.isPending}
              disabledReason={card ? undefined : "Choose a card to pay with"}
              onClick={() => restore.mutate()}
            >
              {money ? `Pay ${money} + VAT & recover` : "Pay & recover"}
            </Button>
          </>
        }
      >
        <div className="flex flex-col gap-3 rounded-xl bg-[#faf6ef] p-3.5 text-sm">
          <div className="flex items-center gap-3">
            <span className="flex-1 text-[#5c5551]">Redemption fee</span>
            <span className="font-semibold text-fg">{money ?? "Calculated on confirmation"}</span>
          </div>
          <div className="flex items-center gap-3 border-t border-border pt-3">
            <span className="flex-1 text-[#5c5551]">Standard renewal (1 year)</span>
            <span className="font-semibold text-fg">included</span>
          </div>
          <div className="flex items-center gap-3 border-t border-border pt-3">
            <span className="flex-1 font-semibold text-fg">Total due today</span>
            <span className="text-lg font-semibold text-fg">{money ? `${money} + VAT` : "—"}</span>
          </div>
        </div>
        <div className="flex items-center gap-3">
          {card && <CardBadge brand={card.brand} />}
          <div className="min-w-0 flex-1">
            <p className="text-sm font-semibold text-fg">{card ? card.label : "No card on file"}</p>
            <p className="text-xs text-fg-subtle">
              {card
                ? !card.saved
                  ? "This payment only, not saved"
                  : card.is_default
                    ? "Your default payment method"
                    : "Used for this recovery"
                : "Add a card to pay"}
            </p>
          </div>
          <Button size="lg" variant="secondary" onClick={() => setPicking(true)}>
            {card ? "Change" : "Add card"}
          </Button>
        </div>
        {card && !card.saved && <RenewalRiskWarning />}
        <Hint tone="warning" title="Time-sensitive">
          {graceLeft !== null && graceLeft > 0
            ? `This price only holds during the ${graceLeft}-day grace period shown. After that, recovery becomes unavailable and the domain enters pending delete.`
            : "Recovery closes soon. After that, the domain enters pending delete and can't be recovered."}
        </Hint>
        <ErrorAlert error={restore.error} />
        <PoweredByStripe provider={methods.data?.provider} />
      </Modal>
      <PaymentMethodModal
        open={open && picking}
        oneTime
        selectedId={card?.id}
        onClose={() => setPicking(false)}
        onChosen={(m) => {
          setChosen(m);
          setPicking(false);
        }}
      />
    </>
  );
}

// ── unrecoverable ───────────────────────────────────────────────────────────
function Unrecoverable({ detail }: { detail: DomainDetail }) {
  const d = detail.domain;
  const { orgId } = useOrganization();
  const qc = useQueryClient();
  const release = daysUntil(d.deleted_at);
  const releaseText =
    release === null ? "soon" : release <= 0 ? "today" : `in ${release} ${release === 1 ? "day" : "days"}`;
  const toast = useToast();
  const disconnect = useMutation({
    mutationFn: () => domainsApi.disconnect(orgId, d.id),
    onSuccess: async () => {
      await qc.invalidateQueries({ queryKey: domainKeys.all(orgId) });
      toast.show({ tone: "success", title: "Website disconnected" });
    },
  });

  return (
    <>
      <Notice
        tone="danger"
        title="The redemption period has ended"
        actions={
          <>
            <Link
              to={`/domains/find?q=${encodeURIComponent(sld(d.fqdn))}`}
              className={buttonClass("brand", "lg")}
            >
              Search for a replacement domain
            </Link>
            <GetHelpLink
              context={{ resourceType: "domain", resourceId: d.fqdn, reason: "Redemption period ended" }}
            />
          </>
        }
      >
        {d.display_name} can no longer be recovered by CortexGrip. It enters the registry's pending-delete
        queue and will be released for anyone to register {releaseText}. This cannot be undone.
      </Notice>
      <TwoColumns
        main={
          <Section title="What happens next">
            {[
              {
                label: "Redemption period ended",
                pill: (
                  <Badge size="md" tone="danger">
                    Completed
                  </Badge>
                ),
              },
              { label: "Pending delete — registry queue", pill: <StepPill state="in_progress" /> },
              {
                label: "Domain released publicly",
                pill: (
                  <Badge size="md" tone="neutral">
                    {inDays(release)}
                  </Badge>
                ),
              },
              {
                label: "Available for anyone to register",
                pill: (
                  <Badge size="md" tone="neutral">
                    After release
                  </Badge>
                ),
              },
            ].map((row, i) => (
              <RuleRow key={row.label} first={i === 0}>
                <p className="min-w-0 flex-1 text-sm font-medium text-[#5c5551]">{row.label}</p>
                {row.pill}
              </RuleRow>
            ))}
          </Section>
        }
        side={
          <Section title="Before it's gone">
            {d.site && (
              <RuleRow first>
                <p className="min-w-0 flex-1 text-sm font-medium text-[#5c5551]">
                  Disconnect it from {d.site.name}
                </p>
                <Button
                  size="lg"
                  variant="secondary"
                  requires={manageOn(detail)}
                  loading={disconnect.isPending}
                  onClick={() => disconnect.mutate()}
                >
                  Do it
                </Button>
              </RuleRow>
            )}
            <RuleRow first={!d.site}>
              <p className="min-w-0 flex-1 text-sm font-medium text-[#5c5551]">
                Register a replacement domain
              </p>
              <Link
                to={`/domains/find?q=${encodeURIComponent(sld(d.fqdn))}`}
                className={buttonClass("secondary", "lg")}
              >
                Do it
              </Link>
            </RuleRow>
            <RuleRow>
              <p className="min-w-0 flex-1 text-sm font-medium text-[#5c5551]">Remove it from My Domains</p>
              <RemoveButton detail={detail} variant="secondary" label="Do it" />
            </RuleRow>
            <ErrorAlert error={disconnect.error} />
          </Section>
        }
      />
    </>
  );
}

// ── transfer away ───────────────────────────────────────────────────────────
function TransferringOut({ detail }: { detail: DomainDetail }) {
  const d = detail.domain;
  const { orgId } = useOrganization();
  const qc = useQueryClient();
  const { run, modal } = useConfirmed();
  const [confirmApprove, setConfirmApprove] = useState(false);
  const answering = pendingOp(detail, "transfer_out");
  const control = has(detail, "transfer_out_control");
  const answeredReason = answering
    ? "We've passed your answer to the registry. Wait for it to be processed."
    : undefined;

  const answer = useMutation({
    mutationFn: (approve: boolean) =>
      run(() => domainsApi.answerTransferOut(orgId, d.id, approve), {
        subtitle: approve
          ? `Approving hands ${d.display_name} to the new registrar — we verify identity first`
          : `Cancelling refuses the transfer of ${d.display_name} — we verify identity first`,
        label: approve ? "Verify & approve" : "Verify & cancel transfer",
      }),
    onSuccess: async () => {
      setConfirmApprove(false);
      await qc.invalidateQueries({ queryKey: domainKeys.all(orgId) });
    },
  });

  const steps: Step[] = [
    { label: "Unlocked for transfer", state: "completed" },
    { label: "Authorization code issued", state: "completed" },
    { label: "New registrar confirmed request", state: "in_progress" },
    { label: "ICANN 5-day approval window", state: "pending" },
    { label: "Transfer completes", state: "not_yet" },
  ];

  return (
    <>
      <Section title="Transfer in progress">
        <p className="max-w-[640px] text-sm text-fg-subtle">
          {answering
            ? "We've passed your answer to the registry — this page updates as soon as it's processed."
            : "Your new registrar has received the transfer request and authorization code. This usually completes within 5-7 days unless you approve it sooner."}
        </p>
        {steps.map((s, i) => (
          <RuleRow key={i} first={i === 0}>
            <p className="min-w-0 flex-1 text-sm font-medium text-[#5c5551]">{s.label}</p>
            <StepPill state={s.state} />
          </RuleRow>
        ))}
        <div className="flex flex-wrap gap-2.5">
          {control && (
            <>
              <Button
                size="lg"
                variant="secondary"
                requires={manageOn(detail)}
                disabledReason={answeredReason}
                loading={answer.isPending && answer.variables === false}
                onClick={() => answer.mutate(false)}
              >
                Cancel transfer
              </Button>
              <Button
                size="lg"
                variant="secondary"
                requires={manageOn(detail)}
                disabledReason={answeredReason}
                onClick={() => setConfirmApprove(true)}
              >
                Approve now
              </Button>
            </>
          )}
          <GetHelpLink context={{ resourceType: "domain", resourceId: d.fqdn, reason: "Transfer stuck" }} />
        </div>
        <ErrorAlert error={confirmApprove ? null : shownError(answer.error)} />
      </Section>
      <Modal
        open={confirmApprove}
        onClose={() => setConfirmApprove(false)}
        title="Approve the transfer now?"
        subtitle={`${d.display_name} moves to the new registrar straight away instead of after the 5-day window`}
        note="This can't be undone from CortexGrip."
        footer={
          <>
            <Button variant="secondary" size="lg" onClick={() => setConfirmApprove(false)}>
              Keep waiting
            </Button>
            <Button variant="brand" size="lg" loading={answer.isPending} onClick={() => answer.mutate(true)}>
              Approve transfer
            </Button>
          </>
        }
      >
        <Hint tone="warning" title="Check your DNS first">
          {d.nameserver_mode === "cortexgrip"
            ? "This domain uses CortexGrip DNS. Once it leaves, renewals and DNS changes happen at your new registrar — make sure your records are set up there."
            : "Once it leaves, renewals and nameserver changes happen at your new registrar."}
        </Hint>
        <ErrorAlert error={shownError(answer.error)} />
      </Modal>
      {modal}
    </>
  );
}

function TransferredOut({ detail }: { detail: DomainDetail }) {
  const d = detail.domain;

  return (
    <>
      <Notice tone="success" title="This domain no longer renews with CortexGrip">
        {d.display_name} completed its transfer. Manage renewals and DNS at your new registrar from now on.
      </Notice>
      <Section title="What this means for your site">
        {d.site_id && (
          <KeyValue variant="detail" label="Website">
            <Badge size="md" tone="warning">
              Still live — only if DNS still points here
            </Badge>
          </KeyValue>
        )}
        <KeyValue variant="detail" label="Billing">
          <Badge size="md" tone="success">
            No further domain charges from CortexGrip
          </Badge>
        </KeyValue>
        <RemoveButton detail={detail} variant="secondary" className="self-start" />
      </Section>
    </>
  );
}

function RemoveButton({
  detail,
  variant,
  label = "Remove from My Domains",
  className,
}: {
  detail: DomainDetail;
  variant: "brand" | "secondary";
  label?: string;
  className?: string;
}) {
  const { orgId } = useOrganization();
  const qc = useQueryClient();
  const navigate = useNavigate();
  const remove = useMutation({
    mutationFn: () => domainsApi.remove(orgId, detail.domain.id),
    onSuccess: async () => {
      await qc.invalidateQueries({ queryKey: domainKeys.all(orgId) });
      navigate("/domains");
    },
  });
  return (
    <>
      <Button
        size="lg"
        variant={variant}
        className={className}
        requires={manageOn(detail)}
        loading={remove.isPending}
        onClick={() => remove.mutate()}
      >
        {label}
      </Button>
      {remove.error ? (
        <span className="w-full">
          <ErrorAlert error={remove.error} />
        </span>
      ) : null}
    </>
  );
}
